/**
 * Keeps the browser's address autofill out of our address forms.
 *
 * The symptom this exists for: filling the Add Location dialog and then
 * clearing it or closing it pops a browser-chrome bubble, "Update address?"
 * with Update / Not now, offering to overwrite the address saved in the user's
 * browser profile. It is Chrome/Edge autofill, not our dialog.
 *
 * ## What does not work, and why
 *
 * `autocomplete="off"` does not stop it. Chromium has ignored `off` on address
 * fields since 2014.
 *
 * An *unrecognized* autocomplete token does not stop it either, which was the
 * first thing tried here. It does opt the field out of being FILLED, but
 * Chromium classifies fields for IMPORT with its own heuristics, run over each
 * field's name, id, placeholder and inferred label. Our labels say "Address",
 * "City", "Postal Code". They will always classify, and we are not making the
 * form less accessible to hide from a browser feature.
 *
 * ## What this does instead
 *
 * Attacks the trigger rather than the classification. Chromium reads a filled
 * address form that is removed from the DOM, or cleared after being filled, as
 * a submitted one. Our dialog does exactly that, twice:
 *
 *   - closing it unmounts the whole form,
 *   - Clear used to remount the search box via a changing `key`, which removes
 *     the very input the user last interacted with.
 *
 * So: blank the marked fields in the DOM *before* the subtree goes away, and
 * stop clearing by remount. There is nothing left to import.
 *
 * This works because the bubble shows address values that React wrote
 * programmatically after a dropdown pick, never typed. Chromium therefore reads
 * the live DOM rather than replaying keystrokes, and an empty DOM has nothing
 * to offer.
 *
 * Marking a field with `noAutofillProps` means both "do not offer to fill this"
 * and "blank me before my dialog goes away".
 */
export const NO_AUTOFILL = 'mdt-no-autofill'

/**
 * Spread onto an address-shaped `<input>`:
 * `<Input {...noAutofillProps} ... />`
 *
 * `data-lpignore` and `data-1p-ignore` ask LastPass and 1Password to leave the
 * field alone as well; they honour their own attributes, not `autocomplete`.
 *
 * Use it on inputs whose value is a property of a record we are editing, never
 * on a field where the user is entering their own details. There, autofill is
 * doing its job.
 */
export const noAutofillProps = {
	autoComplete: NO_AUTOFILL,
	'data-lpignore': 'true',
	'data-1p-ignore': 'true',
} as const

const SELECTOR = `input[autocomplete="${NO_AUTOFILL}"]`

/**
 * Empty every opted-out field inside `root`, in the DOM, synchronously.
 *
 * Call it immediately before the subtree is removed or reset. It deliberately
 * bypasses React: the point is the DOM state at the instant the browser looks,
 * and by then a state update would not have been committed. The component
 * either unmounts straight after (so the divergence is never seen) or clears
 * its own state in the same breath.
 *
 * The native setter is used rather than `el.value = ''` so React's own value
 * tracker is not left believing the field still holds the old string, which
 * would make the next real keystroke look like a no-op change.
 */
export function blankNoAutofillFields(root: HTMLElement | Document | null | undefined): void {
	if (!root) return
	let setter: ((this: HTMLInputElement, v: string) => void) | undefined
	try {
		setter = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value')?.set
	} catch {
		// Exotic environment. Fall back to the plain assignment below.
	}
	root.querySelectorAll<HTMLInputElement>(SELECTOR).forEach(el => {
		if (!el.value) return
		try {
			if (setter) setter.call(el, '')
			else el.value = ''
		} catch {
			// One stubborn field is not worth failing a dialog close over.
		}
	})
}
